import React, { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {
  Store,
  Eye,
  Images,
  X,
  Mail,
  Phone,
  MapPin,
  Building2,
  CheckCircle,
  Clock,
  XCircle,
  Circle,
  FileDown,
  RefreshCcw,
} from "lucide-react";
import "../Sales Person/CreateSalesPerson.css";
import Pagination from "../Pagination";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import LoadingScreen from "../Utils/LoadingScreen";

const compareDatesDesc = (a, b) => {
  const dateA = new Date(a.createdOn).getTime();
  const dateB = new Date(b.createdOn).getTime();
  return dateB - dateA;
};

function OutletManager() {
  const navigate = useNavigate();
  const { api, Products = [], adminId } = useContext(GlobalContext);

  // Correct session fallback (matches how LoginPage stores it)
  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  // Use context first, fallback to session
  const effectiveAdminId = adminId || sessionAdminId;

  const [selectedProduct, setSelectedProduct] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [detailRow, setDetailRow] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [docsModal, setDocsModal] = useState({
    open: false,
    outletId: null,
    data: [],
    loading: false,
  });
  const [statusInfo, setStatusInfo] = useState({});
  const [loadingAction, setLoadingAction] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Prefill product (first item) when Products arrive (1 or many)
  useEffect(() => {
    if (!selectedProduct && Products?.length > 0) {
      setSelectedProduct(Products[0]?.pk_id ?? Products[0]?.id ?? "");
    }
  }, [Products, selectedProduct]);

  const productOptions = useMemo(
    () =>
      (Products || []).map((p) => ({
        value: p.pk_id ?? p.pkId ?? p.id ?? "",
        label: p.product_name ?? p.name ?? "Unknown",
      })),
    [Products]
  );

  // 🔧 KEY FIX: include admin id in deps, and use effectiveAdminId
  useEffect(() => {
    if (effectiveAdminId && selectedProduct) {
      fetchOutlets(effectiveAdminId, selectedProduct, true);
    } else {
      setRows([]);
    }
  }, [effectiveAdminId, selectedProduct]);

  const fetchOutlets = async (adminIdParam, productId, isPageLoad = false) => {
    if (isPageLoad) setPageLoading(true);
    else setLoading(true);

    const loaderDelay = new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const payload = {
        adminId: String(adminIdParam),
        productId: String(productId),
      };

      const resPromise = api.post(`/Admin/v1.0/getOutletDetails`, payload);
      const [res] = await Promise.all([resPromise, loaderDelay]);

      if (res?.data?.responseCode === "00") {
        const data = res.data.data || [];
        const sortedData = data.sort(compareDatesDesc);
        setRows(sortedData);
        // setRows(res.data.data || []);
      } else {
        toast.info(res?.data?.responseMessage || "Failed to fetch outlets");
      }
    } catch (e) {
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const openDetails = (row) => setDetailRow(row);
  const closeDetails = () => setDetailRow(null);

  const openDocs = async (row) => {
    if (!effectiveAdminId || !selectedProduct) {
      toast.info("Select a product first.");
      return;
    }
    const id = row?.pk_outlet_id || row?.outlet_id || row?.id || null;
    setStatusInfo({
      isApproved: row?.status === "Approved" || false,
      isRejected: row?.status === "Rejected" || false,
    });
    setDocsModal((s) => ({
      ...s,
      open: true,
      outletId: id,
      data: [],
      loading: true,
    }));
    try {
      const payload = {
        adminId: String(effectiveAdminId),
        productId: String(selectedProduct),
        outletId: String(id || ""),
      };
      const res = await api.post(`/Admin/v1.0/getOutletDocs`, payload);
      if (res?.data?.responseCode === "00") {
        setDocsModal((s) => ({ ...s, data: res.data.data || [] }));
      } else {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch outlet docs"
        );
      }
    } catch (e) {
      toast.error("Something went wrong!");
    } finally {
      setDocsModal((s) => ({ ...s, loading: false }));
    }
  };

  const handleKycStatusUpdate = async (outletId, action, remark = "") => {
    if (!effectiveAdminId || !selectedProduct) {
      toast.info("Select a product first.");
      return;
    }
    setLoadingAction(action);
    try {
      const payload = {
        adminId: String(effectiveAdminId),
        productId: String(selectedProduct),
        outletId: String(outletId || ""),
        status: action,
        remark: remark,
      };
      const res = await api.post(`/Admin/v1.0/updateKYCStatus`, payload);
      if (res?.data?.responseCode === "00") {
        toast.success(`KYC ${action}d successfully`);
        setStatusInfo((prev) => ({
          ...prev,
          isApproved: action === "Approve" ? true : prev.isApproved,
          isRejected: action === "Reject" ? true : prev.isRejected,
        }));
        if (action === "Reject") {
          setRejectRemark("");
        }
        await fetchOutlets(effectiveAdminId, selectedProduct);
        closeDocs();
      } else {
        toast.error(
          res?.data?.responseMessage || "Failed to update KYC status"
        );
      }
    } catch (e) {
      toast.error("Something went wrong!");
    } finally {
      setLoadingAction(null);
    }
  };

  const closeDocs = () => {
    setDocsModal({ open: false, outletId: null, data: [], loading: false });
    setStatusInfo({});
    setRejectRemark("");
  };

  const filteredRows = useMemo(() => {
    if (!rows?.length) return [];
    if (!search.trim()) return rows;

    const s = search.toLowerCase();

    return rows.filter((r) => {
      const fields = [
        r.outlet_name,
        r.outlet_emailid,
        r.outlet_mobile_no,
        r.salesPersonName,
        r.salespersonMobile,
        r.status,
      ];

      return fields.some((f) => f?.toLowerCase().includes(s));
    });
  }, [rows, search]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredRows.slice(start, end);
  }, [filteredRows, currentPage]);

  if (pageLoading) {
    return (
      <div className="relative min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center rounded-2xl shadow">
        <LoadingScreen />;
      </div>
    );
  }

  return (
    <div className="aoc-csp-screen w-full bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow min-h-[90svh]">
      <div className="aoc-csp-container px-4 py-6 md:py-4 ">
        <div className="aoc-csp-header flex items-center justify-between gap-4 mb-6 md:mb-10 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 shadow-sm">
              <Store className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
                Outlets
              </h1>
            </div>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="min-w-[160px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email..."
                className="aoc-csp-control-new"
              />
            </div>

            <button
              className="btn btn-refresh"
              onClick={() =>
                fetchOutlets(effectiveAdminId, selectedProduct, true)
              }
            >
              <RefreshCcw size={14} />
              <span>Refresh</span>
            </button>

            <button
              className="btn btn-export"
              onClick={() =>
                exportAnyJsonToExcel({
                  data: filteredRows,
                  filePrefix: "outletExport",
                  sheetName: "outletList",
                })
              }
            >
              <FileDown size={14} />
              <span>Export</span>
            </button>

            <div className="min-w-[100px]">
              <label className="aoc-csp-label ml-2">Product</label>
              <div className="aoc-csp-control-new">
                <select
                  className="aoc-csp-input w-[100px] text-sm rounded outline-none"
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  <option value="">Select product</option>
                  {productOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="aoc-csp-card relative rounded-2xl animate-rise">
          <div className="aoc-csp-table-wrapper overflow-x-auto">
            <table className="aoc-csp-table w-full">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Outlet</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Type</th>
                  <th>Sales Person Name</th>
                  <th>Sales Person Code</th>
                  <th>Sales Person Mobile</th>
                  <th>Status</th>
                  <th className="w-[160px] text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <tr key={`sk-${i}`} className="aoc-csp-skel-row">
                        <td>
                          <div className="aoc-csp-skel w-40" />
                        </td>
                        <td>
                          <div className="aoc-csp-skel w-36" />
                        </td>
                        <td>
                          <div className="aoc-csp-skel w-24" />
                        </td>
                        <td>
                          <div className="aoc-csp-skel w-20" />
                        </td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-3">
                            <div className="aoc-csp-skel h-6 w-6 rounded-full" />
                            <div className="aoc-csp-skel h-6 w-6 rounded-full" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : paginatedRows?.length > 0 ? (
                  paginatedRows.map((r, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="text-gray-700">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td className="text-gray-700">{r.createdOn}</td>
                      <td className="text-gray-900 font-medium flex items-center gap-3">
                        <img
                          src={r.outlet_logo}
                          alt={r.outlet_name}
                          className="h-8 w-8 rounded-lg object-cover border border-gray-200"
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                        />
                        {r.outlet_name}
                      </td>
                      <td className="text-gray-700">
                        {r.outlet_emailid || "N/A"}
                      </td>
                      <td className="text-gray-700">{r.outlet_mobile_no}</td>
                      <td className="text-gray-700">{r.outlet_type}</td>
                      <td className="text-gray-700">{r.salesPersonName}</td>
                      <td className="text-gray-700">{r.salesPersonCode}</td>
                      <td className="text-gray-700">{r.salespersonMobile}</td>

                      <td className="text-gray-700">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm ${
                            r.status === "Approved"
                              ? "bg-green-500"
                              : r.status === "Pending"
                              ? "bg-yellow-500"
                              : r.status === "Rejected"
                              ? "bg-red-500"
                              : "bg-gray-400"
                          }`}
                        >
                          {r.status === "Approved" && <CheckCircle size={16} />}
                          {r.status === "Pending" && <Clock size={16} />}
                          {r.status === "Rejected" && <XCircle size={16} />}
                          {!["Approved", "Pending", "Rejected"].includes(
                            r.status
                          ) && <Circle size={16} />}
                          {r.status}
                        </span>
                      </td>

                      <td className="text-center">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            className="aoc-csp-icon-btn text-gray-600 hover:text-gray-900"
                            title="Details"
                            onClick={() => openDetails(r)}
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            className="aoc-csp-icon-btn text-blue-600 hover:text-blue-800"
                            title="Docs"
                            onClick={() => openDocs(r)}
                          >
                            <Images className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="py-10 text-center">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="aoc-csp-empty-pin mb-3" />
                        <p className="text-gray-700 font-medium">
                          No outlets found
                        </p>
                        <p className="text-gray-500 text-sm">
                          {selectedProduct
                            ? "Try changing the product."
                            : "Please select a product."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Modal */}
        {detailRow && (
          <div className="aoc-csp-modal" role="dialog" aria-modal="true">
            <div className="aoc-csp-modal-backdrop" onClick={closeDetails} />
            <div className="aoc-csp-modal-card animate-rise">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={detailRow.outlet_logo}
                    alt={detailRow.outlet_name}
                    className="h-10 w-10 rounded-xl object-cover border border-gray-200"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {detailRow.outlet_name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {detailRow.outlet_type}
                    </p>
                  </div>
                </div>
                <button
                  className="aoc-csp-icon-btn"
                  onClick={closeDetails}
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={detailRow.outlet_emailid}
                />
                <InfoRow
                  icon={<Phone className="h-4 w-4" />}
                  label="Mobile"
                  value={detailRow.outlet_mobile_no}
                />
                <InfoRow
                  icon={<Building2 className="h-4 w-4" />}
                  label="GST"
                  value={detailRow.gst || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="PAN"
                  value={detailRow.pancard || "N/A"}
                />
                <InfoRow
                  // icon={<Mail className="h-4 w-4" />}
                  label="Create Date"
                  value={detailRow.createdOn || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Outlet ID"
                  value={detailRow.pk_outlet_id || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Outlet Type"
                  value={detailRow.outlet_type || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Outlet Name"
                  value={detailRow.outlet_name || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Sales Person Name"
                  value={detailRow.salesPersonName || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Sales Person Code"
                  value={detailRow.salesPersonCode || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Sales Person Mobile"
                  value={detailRow.salespersonMobile || "N/A"}
                />
                <InfoRow
                  // icon={<Hash className="h-4 w-4" />}
                  label="Status"
                  value={detailRow.status || "N/A"}
                />
                <div className="md:col-span-2">
                  <div className="aoc-csp-label mb-1">Address</div>
                  <div className="aoc-csp-control">
                    <span className="aoc-csp-icon">
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </span>
                    <textarea
                      className="aoc-csp-input min-h-[70px]"
                      value={`${detailRow.outlet_address || ""}\nPincode: ${
                        detailRow.outlet_pincode || "N/A"
                      }`}
                      readOnly
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="aoc-csp-label mb-1">Remarks</div>
                  <div className="aoc-csp-control">
                    <textarea
                      className="aoc-csp-input min-h-[80px]"
                      readOnly
                      value={detailRow.remark || "N/A"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Docs Modal */}
        {docsModal.open && (
          <div className="aoc-csp-modal" role="dialog" aria-modal="true">
            <div className="aoc-csp-modal-backdrop" onClick={closeDocs} />
            <div className="aoc-csp-modal-card animate-rise">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Outlet Documents
                  </h3>
                  <p className="text-xs text-gray-500">
                    Outlet ID: {docsModal.outletId || "N/A"}
                  </p>
                </div>
                <button
                  className="aoc-csp-icon-btn"
                  onClick={closeDocs}
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {docsModal.loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="aoc-csp-skel h-28 w-full rounded-xl"
                    />
                  ))}
                </div>
              ) : docsModal.data?.length ? (
                <div className="grid grid-cols-1 gap-6">
                  {docsModal.data.map((doc, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-gray-100 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-gray-700">
                          Outlet #{doc.outlet_id}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DocTile label="PAN" url={doc.pancard} />
                        <DocTile label="GST" url={doc.gst} />
                        <DocTile label="Aadhaar Front" url={doc.aadhar_front} />
                        <DocTile label="Aadhaar Back" url={doc.aadhar_back} />
                        <DocTile label="Outlet Image" url={doc.outlet_image} />
                        <DocTile label="FSSAI" url={doc.fssai} />
                      </div>
                    </div>
                  ))}

                  {docsModal.data?.length > 0 && (
                    <div className="mt-6 pt-2 border-gray-200">
                      {!statusInfo.isRejected && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rejection Remark{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            rows={3}
                            placeholder="Enter reason for rejection..."
                            value={rejectRemark}
                            onChange={(e) => setRejectRemark(e.target.value)}
                          />
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-center gap-4">
                        {!statusInfo.isApproved && (
                          <button
                            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() =>
                              handleKycStatusUpdate(
                                docsModal.outletId,
                                "Approve",
                                ""
                              )
                            }
                            disabled={
                              loadingAction === "Approve" || !docsModal.outletId
                            }
                          >
                            {loadingAction === "Approve"
                              ? "Processing..."
                              : "Approve KYC"}
                          </button>
                        )}

                        {!statusInfo.isRejected && (
                          <button
                            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            onClick={() => {
                              const trimmedRemark = (rejectRemark || "").trim();
                              if (!trimmedRemark) {
                                toast.info("Write rejection reason");
                                return;
                              }
                              handleKycStatusUpdate(
                                docsModal.outletId,
                                "Reject",
                                trimmedRemark
                              );
                            }}
                            disabled={loadingAction === "Reject"}
                          >
                            {loadingAction === "Reject"
                              ? "Processing..."
                              : "Reject KYC"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  No documents found for this outlet.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      {filteredRows.length > itemsPerPage && (
        <div className="flex justify-center pb-5">
          <Pagination
            totalItems={filteredRows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div>
      <div className="aoc-csp-label mb-1">{label}</div>
      <div className="aoc-csp-control">
        <span className="aoc-csp-icon">{icon}</span>
        <input className="aoc-csp-input" value={value || "-"} readOnly />
      </div>
    </div>
  );
}

function DocTile({ label, url }) {
  const isValid =
    !!url && typeof url === "string" && url.trim() !== "" && url !== "string";

  const handleClick = () => {
    if (isValid) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex flex-col">
      <div className="aoc-csp-doc-tile group">
        <div className="aoc-csp-doc-thumb cursor-pointer" onClick={handleClick}>
          {isValid ? (
            <img
              src={url}
              alt={label}
              className="h-full w-full object-cover"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <div className="aoc-csp-doc-empty">No Image</div>
          )}
        </div>
      </div>
      <div className="text-center mt-2 px-2">
        <div className="text-xs font-semibold text-gray-700 py-1 px-2 rounded">
          {label}
        </div>
      </div>
    </div>
  );
}

export default OutletManager;
